import get from 'lodash/get';
import { IM_PAYING_MYSELF } from '@zocdoc-frontend-common/components-insuranceform';
import {
    DENTAL_INSURANCE_SPECIALTIES,
    VISION_INSURANCE_SPECIALTIES,
} from '@zocdoc-frontend-common/utils-constants';
import { isMobileBreakpoint } from '@zocdoc-frontend-common/utils-deviceutils';
import * as Constants from 'src/Booking/components/AppointmentRequirements/constants';
import { hasRequirement } from 'src/Booking/components/AppointmentRequirements/helpers';
import {
    DERMATOLOGIST_SPECIALTIES,
    OBGYN_SPECIALTIES,
    PCP_SPECIALTIES,
} from 'src/constants';

/**
 *
 * @param {Object[]} requirements list of requirement objects
 * @param {Object} professional Object containing information about the professional
 * @param {boolean} isMemberIdAlreadyRequired is member id already required by the provider
 * @param {String} carrierId insurance carrier id string
 * @returns {boolean}
 * @constructor
 */
export const shouldRecordExperiment = ({
    requirements,
    professional,
    isMemberIdAlreadyRequired,
    carrierId,
}) => {
    const specialtyId = get(professional, 'specialtyId');
    const eligibleDoctors = [
        ...PCP_SPECIALTIES,
        ...OBGYN_SPECIALTIES,
        ...DERMATOLOGIST_SPECIALTIES,
    ];
    const isEligibleDoctor = eligibleDoctors.includes(specialtyId);

    const addressRequired = hasRequirement(
        Constants.ADDRESS_CONFIRMATION,
        requirements
    );

    return (
        !isMemberIdAlreadyRequired &&
        isEligibleDoctor &&
        !addressRequired &&
        carrierId !== IM_PAYING_MYSELF
    );
};

/**
 *
 * @param {Object} predictions will have the structure [{carrier : { carrier_id, carrier_name}, plans: [{plan_id, plan_name},...]}]
 * @returns {{predictedPlanId: null, predictedCarrierId: null}}
 */
export const getCarrierAndPlanPrediction = ({ predictions }) => {
    let predictedCarrierId = null;
    let predictedPlanId = null;
    if (predictions) {
        const firstPrediction = predictions[0];
        predictedCarrierId = firstPrediction?.carrier?.carrier_id;
        const predictedPlans = firstPrediction?.plans;
        const firstPredictedPlan = predictedPlans ? predictedPlans[0] : null;
        predictedPlanId = firstPredictedPlan?.plan_id;
    }
    return { predictedCarrierId, predictedPlanId };
};

/***
 * @param {Object} predictions will have the structure [{carrier : { carrier_id, carrier_name}, plans: [{plan_id, plan_name},...]}]
 * @param {number} actualCarrierId insurance carrier id that is in the current state
 * @param {number} actualPlanId insurance plan id that is in the state
 * @param {function} sendMetric function to send metrics
 */
export const sendScanCardMetrics = ({
    predictions,
    actualCarrierId,
    actualPlanId,
    sendMetric,
}) => {
    const { predictedCarrierId, predictedPlanId } = getCarrierAndPlanPrediction(
        { predictions }
    );

    //carrier and plan id must be cast to strings to compare to the insurance cookies
    const parsedCarrierId = predictedCarrierId?.toString();
    const parsedPlanId = predictedPlanId?.toString();

    sendMetric({
        element: 'InsuranceCardExtractionServiceResponse',
        action: 'Parsed insurance card successfully',
        eventDetails: {
            predictedCarrierId,
            predictedPlanId,
        },
    });
    if (parsedPlanId === actualPlanId && parsedCarrierId === actualCarrierId) {
        sendMetric({
            element: 'InsuranceCardExtractionServiceResponse',
            action: 'PredictionMatch',
            eventDetails: {
                carrierId: predictedCarrierId,
                planId: predictedPlanId,
            },
        });
    }
    if (parsedCarrierId !== actualCarrierId) {
        sendMetric({
            element: 'InsuranceCardExtractionServiceResponse',
            action: 'CarrierPredictionDoesNotMatch',
            eventDetails: {
                predictedCarrierId: predictedCarrierId,
                actualCarrierId: actualCarrierId,
            },
        });
    }
    if (parsedPlanId !== actualPlanId) {
        sendMetric({
            element: 'InsuranceCardExtractionServiceResponse',
            action: 'PlanPredictionDoesNotMatch',
            eventDetails: {
                predictedPlanId: predictedPlanId,
                actualPlanId: actualPlanId,
            },
        });
    }
};

export const submitScanCardHelper = ({
    response,
    actualCarrierId,
    actualPlanId,
    onScanInsuranceCardImagesSuccess,
    sendMetric,
}) => {
    const { correlation_id: correlationId, insurances } = response;
    sendScanCardMetrics({
        predictions: insurances,
        actualCarrierId,
        actualPlanId,
        sendMetric,
    });
    onScanInsuranceCardImagesSuccess(correlationId);
};

export const isHealthcareProvider = ({ specialtyId }) => {
    return !(
        DENTAL_INSURANCE_SPECIALTIES.has(specialtyId) ||
        VISION_INSURANCE_SPECIALTIES.has(specialtyId)
    );
};

export const isInsuranceScannerVisible = ({
    specialtyId,
    isScannerExperimentOn,
}) => {
    return (
        isHealthcareProvider({ specialtyId }) &&
        isMobileBreakpoint() &&
        isScannerExperimentOn
    );
};

export const shouldShowPayForSelfError = ({
    specialtyId,
    isScannerExperimentOn,
    insuranceImage,
    carrierId,
}) => {
    return (
        isInsuranceScannerVisible({ specialtyId, isScannerExperimentOn }) &&
        insuranceImage != null &&
        carrierId === IM_PAYING_MYSELF
    );
};
